import React, { FC, memo, useMemo } from 'react';
import { Layer, Rect as KonvaRect, Stage } from 'react-konva';
import classnames from 'classnames';

import { Rect } from '../../graphql/__generated__/graphql';
import { DIFF_COLORS, DiffRectType } from '../diff/components/DiffRect';
import Styles from './BuildSnapshotThumbChangeOverlay.styles';
import { getRectSize } from '../../global/utils';

/**
 * Rough estimation (as polling / event listeners would be too expensive for many items on a page)
 * for max width in pixels for a build snapshot thumb. Used to estimate zoom for cropped image and
 * minimum rect sizes.
 */
const MAX_THUMBNAIL_WIDTH = 256;

export const BuildSnapshotThumbChangeOverlay: FC<
  BuildSnapshotThumbChangeOverlayProps
> = memo((props) => {
  const { width, height, diffClusters, ignoreRegions } = props;

  const {
    adjustedClusters,
    adjustedIgnoreRegions,
  } = useMemo<{
    adjustedClusters: Rect[], adjustedIgnoreRegions: Rect[]
  }>(() => {
    // Estimates 'zoom' on a page by taking max thumbnail width over total native image size. Since
    // we're cropping to width, we can use this as a static point of reference for image 'zoom.'
    const estimatedZoom = MAX_THUMBNAIL_WIDTH / width;
    const isInPreview = (rect: Rect) => (
      rect.y <= height
    );

    return {
      adjustedClusters: diffClusters
        .filter(isInPreview)
        .map((rect) => getRectSize(rect, estimatedZoom)),
      adjustedIgnoreRegions: ignoreRegions
        .filter(isInPreview)
        .map((rect) => getRectSize(rect, estimatedZoom)),
    };
  }, [diffClusters, height, width, ignoreRegions]);

  return (
    <div
      className={classnames(
        Styles.wrapper,
        'absolute top-0 left-0 w-full h-full pointer-events-none',
      )}
    >
      <Stage
        className="w-full h-auto"
        height={height}
        width={width}
      >
        <Layer>
          {adjustedIgnoreRegions.map((diff, index) => (
            <KonvaRect
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              fill={DIFF_COLORS[DiffRectType.IGNORED].bg}
              height={diff.height}
              width={diff.width}
              x={diff.x}
              y={diff.y}
            />
          ))}
          {adjustedClusters.map((diff, index) => (
            <KonvaRect
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              fill={DIFF_COLORS[DiffRectType.CHANGED].bg}
              height={diff.height}
              width={diff.width}
              x={diff.x}
              y={diff.y}
            />
          ))}
        </Layer>
      </Stage>
    </div>
  );
});

export interface BuildSnapshotThumbChangeOverlayProps {
  diffClusters: Rect[];
  ignoreRegions: Rect[];
  width: number;
  height: number;
}
